import { ChevronDown, LogOut, User as UserIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSession } from "@/hooks/useSession";
import { useRole } from "@/lib/role-context";
import { signOut } from "@/lib/auth";
import type { Role } from "@/data/role-defaults";

const ROLE_LABELS: Record<Role, string> = {
  programme: "Programme lead",
  district: "District lead",
  school: "School leader",
  teacher: "Teacher",
};

function initialsFrom(displayName: string | undefined): string {
  if (!displayName) return "";
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!first) return "";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return ((first[0] ?? "") + (last?.[0] ?? "")).toUpperCase();
}

export function UserMenu() {
  const { data: session, usesPortalLoginPage } = useSession();
  const role = useRole();
  const navigate = useNavigate();

  if (!session?.authenticated || !session.user) return null;

  const { displayName, email } = session.user;
  const initials = initialsFrom(displayName);
  const roleLabel = role ? ROLE_LABELS[role] : null;

  const handleSignOut = () => {
    signOut();
    if (usesPortalLoginPage) {
      navigate("/login", { replace: true });
    } else if (session.loginUrl) {
      window.location.assign(session.loginUrl);
    } else {
      navigate("/login", { replace: true });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Account menu for ${displayName}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            minHeight: 44,
            padding: "4px 10px 4px 4px",
            background: "transparent",
            border: "1px solid var(--border-default)",
            borderRadius: 999,
            cursor: "pointer",
            font: "inherit",
            color: "var(--fg-1)",
          }}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 hover:bg-[var(--surface-pressed)] transition-colors"
        >
          <span
            aria-hidden
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "var(--ink-150)",
              color: "var(--fg-2)",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.02em",
              flexShrink: 0,
            }}
          >
            {initials || <UserIcon size={18} />}
          </span>
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.1, textAlign: "left", minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--fg-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180 }}>
              {displayName}
            </span>
          </span>
          <ChevronDown aria-hidden size={16} style={{ color: "var(--fg-3)", flexShrink: 0 }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-[220px]">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--fg-1)" }}>{displayName}</span>
          {email && (
            <span style={{ fontSize: 12, fontWeight: 400, color: "var(--fg-3)" }}>{email}</span>
          )}
          {roleLabel && (
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--fg-3)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 2 }}>
              {roleLabel}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleSignOut}>
          <LogOut aria-hidden />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
